import React, { useCallback, useMemo } from "react";
import { Button, Col, Divider, Empty, Flex, Form, Grid, Input, notification, Popconfirm, Row, Select, Skeleton, Typography } from "antd"

import { PropriedadesDoPopConfirm } from "../../../../Defaults";
import { SessaoContext } from "../../../../Contexts/SessaoContext";
import { Permissoes } from "../../../../Constants/Permissoes";
import { Restrito } from "../../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../../Components/Conjunto";
import { Ordenar } from "../../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../../Hooks/useGetParameterOnRoutes";
import { useContext, useEffect, useState } from "react";
import { DestinoService } from "../../../../Services/Domain/Viagem/DestinoService";
import { useForm } from "antd/lib/form/Form";

import { useMutation, useQuery } from "@tanstack/react-query";

import AdicionarAjuda, { ITutorial } from "../../../../Components/AdicionarAjuda";
import { PropriedadesDeNotificacao } from "../../../../Utils/Notificacoes";
import { TypeDestino } from "../../../../Models/Domain/Viagem/Destino";

import * as Tutoriais from "./destino.tutorial"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";

const ListagemDeDestinos: React.FC<{
    aoEditar?: (destino: TypeDestino) => any,
    aoAdicionar?: () => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (destino: TypeDestino) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const viagemService = new DestinoService();

    interface IFiltro {
        ativo?: boolean,
        nome?: string,
        descricao?: string,
    }

    const [filtro, setFiltro] = useState<IFiltro>({});

    const destinosDisponiveis = useQuery({
        queryKey: ["listarDestinos", filtro],
        gcTime: 0,
        queryFn: () => viagemService.listarDestinos({
            ativo: filtro.ativo,
            nome: filtro.nome,
            descricao: filtro.descricao
        }),
        enabled: true,
    });

    const onFinish = (valores: IFiltro) => {
        setFiltro({
            ...valores
        });
    }

    const [form] = useForm<IFiltro>();

    const adicionar = permissoes?.includes(Permissoes.CriarDestino);
    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([])

    return (
        <React.Fragment>
            <Form<IFiltro> form={form} onFinish={onFinish} layout="vertical" autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro}>
                    <Col xs={24} xl={4} id={Tutoriais.IDs.filtroDeNome}>
                        <Form.Item<IFiltro> name="nome" label="Nome">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={8} id={Tutoriais.IDs.filtroDeDescricao}>
                        <Form.Item<IFiltro> name="descricao" label="Descrição">
                            <Input placeholder="Todos" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={4} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={adicionar ? 12 : 24} xl={adicionar ? 2 : 5} style={{ alignItems: "end", display: "flex" }}>
                        <Form.Item style={{ width: "100%" }}>
                            <Button
                                type="primary"
                                id={Tutoriais.IDs.botaoListar}
                                htmlType="submit"
                                disabled={destinosDisponiveis.isFetching}
                                style={{ width: "100%" }}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionar}>
                        <Col xs={12} xl={3} style={{ alignItems: "end", display: "flex" }}>
                            <Form.Item style={{ width: "100%" }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeDestino>
                        bordered
                        rowKey={(k) => k.codigo!}
                        locale={{ emptyText: "Nenhum destino encontrado." }}
                        loading={destinosDisponiveis.isFetching}
                        dataSource={destinosDisponiveis.data?.data}
                        addTitleOn="lg"
                        expandable={{
                            showExpandColumn: false,
                            expandedRowRender: (record) => (
                                <Typography>
                                    <React.Fragment>
                                        <Typography.Title level={4} style={{ marginTop: 0 }}>
                                            {" "}
                                            Descrição{" "}
                                        </Typography.Title>
                                        <Typography.Text> {record.descricao?.trim() ?? <Empty description="Nenhuma descrição adicionada" />} </Typography.Text>
                                    </React.Fragment>
                                </Typography>
                            ),
                            rowExpandable: () => true,
                            expandedRowKeys: chavesDasLinhasExpandidas,
                        }}
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 3 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 24, sm: 12, lg: 3 },
                                key: "ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },

                            {
                                title: "Nome",
                                dataIndex: ["nome"],
                                breakpoints: { xs: 24, lg: 5 },
                                key: "nome",
                                sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                            },

                            {
                                title: "Descrição",
                                dataIndex: ["descricao"],
                                breakpoints: { xs: 24, lg: 8 },
                                key: "descricao",
                                render: (v: string) => {
                                    if (v == undefined) return "N/A";

                                    if (v.trim().length < 200) return v;
                                    return v.trim().slice(0, 200) + " [...]";
                                },
                            },

                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                hidden: [Permissoes.AtualizarDestino, Permissoes.DesativarDestino, Permissoes.AtivarDestino].every(
                                    (p) => !permissoes?.includes(p)
                                ),
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={[
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                                    onClick={() => {
                                                        if (!item.codigo) return;

                                                        if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                            setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                                        } else {
                                                            setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                                        }
                                                    }}
                                                ></Button>,
                                                ...(() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtualizarDestino) && (
                                                                <Link
                                                                    to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                    onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                                >
                                                                    <Button
                                                                        className={Tutoriais.Classes.botaoEditar}
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            props.aoEditar?.(item);
                                                                        }}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                </Link>
                                                            ),
                                                            permissoes?.includes(Permissoes.DesativarDestino) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("desativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;

                                                                        viagemService
                                                                            .desativarDestino(item.codigo)
                                                                            .then(() => {
                                                                                destinosDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="default" danger size="small" id={Tutoriais.Classes.botaoDesativar}>
                                                                        Desativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarDestino) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("ativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;

                                                                        viagemService
                                                                            .ativarDestino(item.codigo)
                                                                            .then(() => {
                                                                                destinosDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="dashed" size="small">
                                                                        Reativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    }
                                                })(),
                                            ]}
                                        />
                                    );
                                },
                            },
                        ]}
                        
                        csv={{
                            enabled: true,
                            filename: "destinos.csv",
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda tutoriais={[].filter((v) => v != false) as ITutorial[]} />
        </React.Fragment>
    );

}

const CadastroDeDestino: React.FC<{
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any,
    destinoEmAlteracao?: TypeDestino
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;
    const destinoService = new DestinoService();

    const { destinoEmAlteracao } = props;

    useEffect(() => {
        if (destinoEmAlteracao != undefined) {
            form.setFieldsValue(destinoEmAlteracao)
        }
    }, [destinoEmAlteracao])

    const [form] = useForm<TypeDestino>();

    const inserirDestino = useMutation({
        mutationFn: (payload: TypeDestino) => destinoService.inserirDestino(payload),
        onSuccess() {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })

            props.depoisDeFinalizar?.();
        },
    })

    const atualizarDestino = useMutation({
        mutationFn: (payload: TypeDestino) => destinoService.atualizarDestino(payload),
        onSuccess() {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro atualizado com sucesso.')
            })

            props.depoisDeFinalizar?.();
        },
    })

    const onFinish = (valores: TypeDestino) => {
        const estaEditando = destinoEmAlteracao != undefined;
        if (estaEditando) {
            atualizarDestino.mutate({ ...destinoEmAlteracao, ...valores, });
        } else {
            inserirDestino.mutate(valores);
        }
    }

    return <React.Fragment>
        <Form<TypeDestino> form={form} onFinish={onFinish} layout="vertical" autoComplete="off"
            validateTrigger={"onBlur"}
        >
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Form.Item<TypeDestino>
                        name={"nome"}
                        label={"Nome"}
                        rules={[{ required: true, message: "É necessário específicar o nome!" }]}
                    >
                        <Input placeholder="Nome" />
                    </Form.Item>
                </Col>

                <Col xs={24}>
                    <Form.Item<TypeDestino>
                        name={"descricao"}
                        label={"Descrição"}
                        rules={[{max: 1000, validateTrigger: "onChange", message: "A descrição não pode ser superior a 1000 caracteres"}]}
                    >
                        <TextArea
                            placeholder="Id aliquip exercitation exercitation laboris ad ut eiusmod nulla aliquip sint."
                            style={{resize: "none"}}
                            rows={4}
                            count={{show: true, max: 1000 }}
                        />
                    </Form.Item>
                </Col>

                <Col span={24}>
                    <Flex align="center" justify="center" gap={16}>
                        {props.aoCancelar &&
                            <Button danger type="primary" key={"cancel"} onClick={() => { props.aoCancelar && props.aoCancelar() }}>
                                Cancelar
                            </Button>
                        }
                        <Button type="primary" key={"submit"} htmlType="submit">
                            Salvar
                        </Button>
                    </Flex>
                </Col>

            </Row>
        </Form>

        <AdicionarAjuda tutoriais={[]} />

    </React.Fragment>
}

export const Destino: React.FC<{}> = (props) => {
    const [destinoEmAlteracao, setDestinoEmAlteracao] = useState<TypeDestino | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterDestino = useMutation({
        mutationFn: (id: number) => new DestinoService().listarDestinos({ codigo: id, ativo: true }),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setDestinoEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarDestinos, Permissoes.CriarDestino, Permissoes.AtualizarDestino, Permissoes.AtivarDestino, Permissoes.DesativarDestino])
    useWatchParameterOnRoutes({
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))],
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterDestino.mutate(Number(param)); },
    })

    return <React.Fragment>
        <>
            <h2 style={{ marginTop: 0 }}>
                {
                    rotaDeVisualizacao ? "Detalhes do Destino" : rotaDeEdicao ? "Edição do Destino" : "Cadastro de Destino"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarDestinos) &&
                    <Route path="*" index element={<ListagemDeDestinos
                        hrefDoBotaoAdicionar={raiz + "/cadastro"}
                        hrefDoBotaoEditar={(item) => {
                            if (item.codigo) {
                                return raiz + `/editar?id=${item.codigo}`
                            }
                        }}

                        aoEditar={(item) => {
                            setDestinoEmAlteracao(item);
                        }}
                    />} />
                }

                {
                    permissoes?.includes(Permissoes.CriarDestino) &&
                    <Route path="/cadastro" element={<CadastroDeDestino
                        aoCancelar={() => { navigate(raiz) }}
                        depoisDeFinalizar={() => { navigate(raiz) }}
                    />} />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarDestino) &&
                    <Route path="/editar" element={
                        destinoEmAlteracao == undefined ?
                            <Skeleton active />
                            :
                            <CadastroDeDestino
                                destinoEmAlteracao={destinoEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                        } 
                    />
                }
            </Routes>

        </>
    </React.Fragment>
}